import { useEffect } from 'react';

export interface PageSeoProps {
  title: string;
  description?: string;
  canonicalUrl?: string;
  ogType?: 'website' | 'article' | 'profile' | 'book';
  ogImage?: string;
  ogImageAlt?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  keywords?: string[] | string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  structuredData?: Record<string, any> | Array<Record<string, any>>;
  noindex?: boolean;
}

const DEFAULT_TITLE_SUFFIX = 'UIBN – Universal Indonesian Book Number';
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=1200&auto=format&fit=crop&q=80';
const DEFAULT_DESCRIPTION =
  'Platform Universal Indonesian Book Number (UIBN). Sistem nomor unik buku resmi, direktori penulis & penerbit terverifikasi, e-sertifikat, dan pelestarian karya literasi Indonesia.';

/**
 * Helper to update or create a <meta> tag
 */
function setMetaTag(attrName: 'name' | 'property', attrValue: string, content: string | undefined | null) {
  if (typeof document === 'undefined') return;

  let element = document.querySelector(`meta[${attrName}="${attrValue}"]`) as HTMLMetaElement | null;

  if (!content) {
    if (element) element.remove();
    return;
  }

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attrName, attrValue);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

/**
 * Helper to update or create a <link> tag (e.g. canonical)
 */
function setLinkTag(rel: string, href: string | undefined | null) {
  if (typeof document === 'undefined') return;

  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;

  if (!href) {
    if (element) element.remove();
    return;
  }

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
}

/**
 * Dynamic SEO hook to manage Title, Meta, OpenGraph, Twitter Cards, and JSON-LD schema re-activity.
 */
export function usePageSeo({
  title,
  description = DEFAULT_DESCRIPTION,
  canonicalUrl,
  ogType = 'website',
  ogImage = DEFAULT_IMAGE,
  ogImageAlt,
  twitterCard = 'summary_large_image',
  keywords,
  author,
  publishedTime,
  modifiedTime,
  structuredData,
  noindex = false,
}: PageSeoProps) {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    // 1. Title formatting
    const formattedTitle = title.includes('UIBN')
      ? title
      : `${title} | ${DEFAULT_TITLE_SUFFIX}`;
    document.title = formattedTitle;

    // 2. Canonical URL
    const currentCanonical = canonicalUrl || window.location.href.split('?')[0].split('#')[0];
    setLinkTag('canonical', currentCanonical);

    // 3. Core Meta Tags
    setMetaTag('name', 'description', description);
    setMetaTag(
      'name',
      'keywords',
      Array.isArray(keywords) ? keywords.join(', ') : keywords || 'UIBN, katalog buku, nomor unik buku, penerbit, penulis, registrasi buku indonesia'
    );
    setMetaTag('name', 'author', author || 'UIBN Indonesia');
    setMetaTag('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');

    // 4. OpenGraph Tags
    setMetaTag('property', 'og:title', formattedTitle);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:url', currentCanonical);
    setMetaTag('property', 'og:type', ogType);
    setMetaTag('property', 'og:image', ogImage);
    setMetaTag('property', 'og:image:alt', ogImageAlt || title);
    setMetaTag('property', 'og:site_name', 'UIBN Indonesia');
    setMetaTag('property', 'og:locale', 'id_ID');

    if (publishedTime) {
      setMetaTag('property', 'article:published_time', publishedTime);
    }
    if (modifiedTime) {
      setMetaTag('property', 'article:modified_time', modifiedTime);
    }
    if (author) {
      setMetaTag('property', 'article:author', author);
    }

    // 5. Twitter Card Tags
    setMetaTag('name', 'twitter:card', twitterCard);
    setMetaTag('name', 'twitter:title', formattedTitle);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', ogImage);
    if (ogImageAlt || title) {
      setMetaTag('name', 'twitter:image:alt', ogImageAlt || title);
    }

    // 6. Inject / Update JSON-LD Script tag
    const scriptId = 'uibn-dynamic-jsonld';
    let scriptTag = document.getElementById(scriptId) as HTMLScriptElement | null;

    if (structuredData) {
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.id = scriptId;
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      scriptTag.text = JSON.stringify(structuredData);
    } else if (scriptTag) {
      scriptTag.remove();
    }

    // Cleanup on unmount / navigation change
    return () => {
      // Keep title and metadata stable, remove specific dynamic jsonld if unmounted without replacement
    };
  }, [
    title,
    description,
    canonicalUrl,
    ogType,
    ogImage,
    ogImageAlt,
    twitterCard,
    keywords,
    author,
    publishedTime,
    modifiedTime,
    structuredData,
    noindex,
  ]);
}
